import { ChangeDetectionStrategy, Component, computed, effect, EventEmitter, input, Input, OnInit, Output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { DynamicFlowLocationAnswer } from '../../model/dynamic-flow.types';
import { PlaceDetails } from '@app/core/services/google-maps.service';
import { GoogleMapComponent } from '@app/shared/ui/google-map/google-map.component';
import { SharedModule } from '@app/shared/shared.module';
import { AutoCompleteComponent } from '@app/shared/components/autocomplete/autocomplete.component';
import { AutocompleteItemModel } from '@app/shared/components/autocomplete/autocomplete-data.model';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'c-location-step',
  imports: [GoogleMapComponent, SharedModule, AutoCompleteComponent, TranslocoPipe],
  templateUrl: './location-step.component.html',
  styleUrl: './location-step.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationStepComponent implements OnInit {
  location = input<DynamicFlowLocationAnswer | null>(null);
  title = input<string>();
  description = input<string>();
  @Input() locationSuggestions: AutocompleteItemModel[] = [];
  showErrors = input<boolean>(false);
  currentLocationUsed = input<boolean>(false);
  @Output() locationSearch = new EventEmitter<string>();
  @Output() locationSelected = new EventEmitter<AutocompleteItemModel>();
  @Output() locationChanged = new EventEmitter<DynamicFlowLocationAnswer | null>();
  @Output() currentLocationRequested = new EventEmitter<void>();

  form = new FormGroup({
    location: new FormControl('', Validators.required),
    distanceInKm: new FormControl(10, Validators.required),
  });

  private selectedAddress = signal<PlaceDetails>(null);
  // The address the step fills in itself for the current location, which mustn't start a place search.
  private currentLocationAddress: string | null = null;
  mapCenter = signal<google.maps.LatLngLiteral>(null);
  zoom = signal<number>(10);
  private distanceInKm = toSignal(this.form.controls.distanceInKm.valueChanges.pipe(map((v) => Number(v) || 10)), {
    initialValue: 10,
  });
  private formValid = toSignal(this.form.statusChanges.pipe(map((s) => s === 'VALID')), { initialValue: false });
  mapRadius = computed(() => this.distanceInKm() * 1000);

  ngOnInit(): void {
    const location = this.location();
    if (!location) return;

    this.initializeLocation(
      {
        city: location.city,
        coordinates: { lat: location.coordinates.latitude, lng: location.coordinates.longitude },
        country: location.country,
        countryCode: location.countryCode,
        postalCode: location.postalCode,
        street: location.street,
      },
      location.distanceInKm,
    );
  }

  private initializeLocation(details: PlaceDetails, distanceInKm = 10): void {
    this.mapCenter.set(details.coordinates);
    this.selectedAddress.set(details);
    this.form.controls.location.patchValue(this.resolveDisplayAddress(details), { emitEvent: false });
    this.form.controls.distanceInKm.setValue(distanceInKm);
    this.form.updateValueAndValidity();
  }

  constructor() {
    this.form.controls.location.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((value) => value !== this.currentLocationAddress),
        takeUntilDestroyed()
      )
      .subscribe((value) => this.locationSearch.emit(value));

    // Existing c-input & c-autocomplete only shows errors for touched field
    effect(() => {
      if (this.showErrors()) {
        untracked(() => this.form.markAllAsTouched());
      }
    });

    effect(() => {
      const center = this.mapCenter();
      const distanceInKm = this.distanceInKm();
      const isValid = this.formValid();
      const address = this.selectedAddress();

      untracked(() => {
        if (isValid) {
          this.locationChanged.emit({
            city: address?.city,
            distanceInKm,
            coordinates: { latitude: center.lat, longitude: center.lng },
            country: address?.country,
            countryCode: address?.countryCode,
            postalCode: address?.postalCode,
            street: address?.street,
          });
        } else {
          this.locationChanged.emit(null);
        }
      });
    });
  }

  requestCurrentLocation(): void {
    this.currentLocationRequested.emit();
  }

  selectLocation(item: AutocompleteItemModel): void {
    this.form.controls.location.setValue(item.label, { emitEvent: false });
    this.locationSelected.emit(item);
  }

  updatePlaceDetails(details: PlaceDetails): void {
    this.selectedAddress.set(details);
    this.mapCenter.set(details.coordinates);
  }

  applyCurrentLocation(details: PlaceDetails): void {
    this.currentLocationAddress = this.resolveDisplayAddress(details);
    this.form.controls.location.setValue(this.currentLocationAddress);
    this.updatePlaceDetails(details);
  }

  private resolveDisplayAddress(details: PlaceDetails): string {
    const parts = [details.street, details.city, details.country].filter(Boolean);
    return parts.length ? parts.join(', ') : `${details.coordinates.lat}, ${details.coordinates.lng}`;
  }
}
